import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, MapPin, Clock, Mail, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SERVICES = [
  "Sealcoating",
  "Landscaping",
  "Lawn Care",
  "Spring & Fall Cleanups",
  "Tree Service / Land Clearing",
  "Snow Removal / Ice Management",
  "Material Delivery",
  "Other / Multiple",
];

const MESSAGE_PLACEHOLDERS = {
  "Sealcoating":
    "Driveway is about 1,200 sq ft, last sealed 4 years ago. Looking for a quote on sealcoating and crack repair.",
  "Landscaping":
    "Need help refreshing the mulch beds in front of the house and shaping some overgrown shrubs. Also open to a design idea for the side yard.",
  "Lawn Care":
    "About 1/2 acre of lawn. Looking for weekly mowing + edging + blow-off, and interested in a fall aeration & seeding quote.",
  "Spring & Fall Cleanups":
    "Looking for a full fall cleanup — leaf removal from the lawn and beds, plus a quick pass on the gutters. Property is roughly 1 acre.",
  "Tree Service / Land Clearing":
    "Have a dead oak tree in the backyard that needs to come down safely, plus some overgrown brush along the fence line to clear.",
  "Snow Removal / Ice Management":
    "Looking for a seasonal contract — driveway + walkway plowing and salting. Residential, ~100 ft driveway.",
  "Material Delivery":
    "Need about 5 yards of brown mulch delivered. If possible, dumped near the driveway so we can spread it ourselves.",
  "Other / Multiple":
    "Tell us about the project — what you need, approximate size, and your timeline.",
};

const FIELD =
  "w-full h-12 px-4 rounded-lg bg-[var(--rr-bg)] border border-white/10 text-white placeholder:text-[var(--rr-text-muted)] focus:outline-none focus:border-[var(--rr-primary)] focus:ring-2 focus:ring-[var(--rr-primary)]/30 transition";

export default function QuoteForm({ defaultService }) {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    service: defaultService && SERVICES.includes(defaultService) ? defaultService : SERVICES[0],
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.address || !form.service) {
      toast.error("Please fill in name, email, phone, address, and service.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/quotes`, form);
      toast.success("Quote request received — we'll be in touch within 24 hours.");
      setDone(true);
      setForm({
        name: "",
        email: "",
        phone: "",
        address: "",
        service: defaultService && SERVICES.includes(defaultService) ? defaultService : SERVICES[0],
        message: "",
      });
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        "Something went wrong. Please call us at 610.994.0500.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="quote"
      data-testid="quote-section"
      className="relative py-24 md:py-32 bg-[var(--rr-bg-deep)]"
    >
      <div className="mx-auto max-w-7xl px-6 md:px-10 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
          {/* Left: contact info */}
          <div className="lg:col-span-5 reveal">
            <p className="overline">Get in touch</p>
            <h2 className="mt-4 font-display text-4xl md:text-5xl font-semibold tracking-tighter text-white">
              Let's talk about
              <br />
              <span className="text-[var(--rr-accent)]">your property.</span>
            </h2>
            <p className="mt-6 text-lg text-[var(--rr-text-muted)] leading-relaxed">
              Free estimates · Fast turnaround · No pressure. Tell us what you
              need and we'll get back to you within 24 hours.
            </p>

            <div className="mt-10 space-y-1">
              <a
                href="tel:+16109940500"
                data-testid="contact-phone"
                className="group flex items-center gap-5 py-5 border-b border-white/5"
              >
                <span className="h-11 w-11 rounded-full bg-[var(--rr-primary)]/15 text-[var(--rr-accent)] flex items-center justify-center">
                  <Phone className="h-4 w-4" />
                </span>
                <span>
                  <span className="block text-xs uppercase tracking-[0.18em] text-[var(--rr-text-muted)]">
                    Call
                  </span>
                  <span className="block font-display text-2xl font-semibold text-white group-hover:text-[var(--rr-accent)] transition-colors">
                    610.994.0500
                  </span>
                </span>
              </a>

              <div className="flex items-center gap-5 py-5 border-b border-white/5">
                <span className="h-11 w-11 rounded-full bg-[var(--rr-primary)]/15 text-[var(--rr-accent)] flex items-center justify-center">
                  <MapPin className="h-4 w-4" />
                </span>
                <span>
                  <span className="block text-xs uppercase tracking-[0.18em] text-[var(--rr-text-muted)]">
                    Office
                  </span>
                  <span className="block text-base text-white mt-0.5">
                    555 2nd Ave, Collegeville, PA 19426
                  </span>
                </span>
              </div>

              <div className="flex items-center gap-5 py-5">
                <span className="h-11 w-11 rounded-full bg-[var(--rr-primary)]/15 text-[var(--rr-accent)] flex items-center justify-center">
                  <Clock className="h-4 w-4" />
                </span>
                <span>
                  <span className="block text-xs uppercase tracking-[0.18em] text-[var(--rr-text-muted)]">
                    Hours
                  </span>
                  <span className="block text-base text-white mt-0.5">
                    Mon – Sat · 8:00 AM – 5:00 PM
                  </span>
                </span>
              </div>
            </div>
          </div>

          {/* Right: form */}
          <div className="lg:col-span-7 reveal">
            <form
              onSubmit={onSubmit}
              data-testid="quote-form"
              className="bg-[var(--rr-surface)] border border-white/5 rounded-2xl p-6 md:p-10"
            >
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs uppercase tracking-[0.18em] text-[var(--rr-text-muted)] mb-2">Full name</label>
                  <input
                    data-testid="quote-input-name"
                    type="text"
                    value={form.name}
                    onChange={update("name")}
                    placeholder="Jane Doe"
                    required
                    className={FIELD}
                  />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-[0.18em] text-[var(--rr-text-muted)] mb-2">Phone</label>
                  <input
                    data-testid="quote-input-phone"
                    type="tel"
                    value={form.phone}
                    onChange={update("phone")}
                    placeholder="(610) 555-0123"
                    required
                    className={FIELD}
                  />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-[0.18em] text-[var(--rr-text-muted)] mb-2">Email</label>
                  <input
                    data-testid="quote-input-email"
                    type="email"
                    value={form.email}
                    onChange={update("email")}
                    placeholder="jane@email.com"
                    required
                    className={FIELD}
                  />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-[0.18em] text-[var(--rr-text-muted)] mb-2">Property address</label>
                  <input
                    data-testid="quote-input-address"
                    type="text"
                    value={form.address}
                    onChange={update("address")}
                    placeholder="123 Main St, Collegeville"
                    required
                    className={FIELD}
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-xs uppercase tracking-[0.18em] text-[var(--rr-text-muted)] mb-2">Service needed</label>
                  <select
                    data-testid="quote-input-service"
                    value={form.service}
                    onChange={update("service")}
                    className={`${FIELD} appearance-none bg-[length:16px] bg-[right_1rem_center] bg-no-repeat`}
                    style={{
                      backgroundImage:
                        "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%239CA3AF' stroke-width='2'><path d='M19 9l-7 7-7-7'/></svg>\")",
                    }}
                  >
                    {SERVICES.map((s) => (
                      <option key={s} value={s} className="bg-[var(--rr-bg)] text-white">
                        {s}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-xs uppercase tracking-[0.18em] text-[var(--rr-text-muted)] mb-2">Tell us about the job</label>
                  <textarea
                    data-testid="quote-input-message"
                    value={form.message}
                    onChange={update("message")}
                    rows={4}
                    placeholder={MESSAGE_PLACEHOLDERS[form.service] || MESSAGE_PLACEHOLDERS["Other / Multiple"]}
                    className={`${FIELD} h-auto py-3 resize-none`}
                  />
                </div>
              </div>

              <div className="mt-7 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <p className="text-xs text-[var(--rr-text-muted)]">
                  We typically respond within 24 hours. For urgent jobs, please call.
                </p>
                <button
                  type="submit"
                  data-testid="quote-form-submit"
                  disabled={submitting}
                  className="inline-flex items-center justify-center gap-2 h-12 px-7 rounded-full bg-[var(--rr-primary)] hover:bg-[var(--rr-primary-hover)] text-white font-semibold transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" /> Sending...
                    </>
                  ) : done ? (
                    <>
                      <Mail className="h-4 w-4" /> Sent — Send another?
                    </>
                  ) : (
                    "Request Quote"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
